import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { LogoMark } from "./LogoMark";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT } from "@/i18n/translations";

const SECTIONS = [
  { key: "products", href: "#products" },
  { key: "enquiry", href: "#enquiry" },
  { key: "contact", href: "#contact" },
];

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      className={`flex items-center rounded-full border border-[rgba(20,112,63,0.3)] p-0.5 ${className}`}
      data-testid="language-toggle"
      role="group"
      aria-label="Language"
    >
      {[
        { code: "en", label: "EN" },
        { code: "hi", label: "हिं" },
      ].map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`lang-${l.code}`}
          aria-pressed={lang === l.code}
          className={`px-3 py-1.5 rounded-full font-mono-spec text-[10px] tracking-[0.12em] transition-colors duration-300 ${
            lang === l.code
              ? "bg-[#14703F] text-white font-semibold"
              : "text-[#51685A] hover:text-[#12261A]"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const NavigationHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t, lang } = useLanguage();
  const specHref = lang === "hi" ? CONTACT.specSheetHi : CONTACT.specSheet;
  const specName = lang === "hi" ? "Aaravya-Impex-Gurjan-Spec-Sheet-Hindi.pdf" : "Aaravya-Impex-Gurjan-Spec-Sheet.pdf";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-[rgba(20,112,63,0.16)] shadow-[0_4px_30px_rgba(20,112,63,0.06)]"
          : "bg-white/0 border-b border-transparent"
      }`}
      data-testid="nav-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
          <LogoMark size={36} />
          <div className="leading-tight">
            <span className="font-serif-display text-lg tracking-wide text-[#12261A] block">
              Aaravya <span className="text-[#14703F]">Impex</span>
            </span>
            <span className="font-mono-spec text-[9px] tracking-[0.3em] uppercase text-[#8CA594]">
              {t.nav.est}
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {SECTIONS.map((s) => (
            <a
              key={s.href}
              href={s.href}
              data-testid={`nav-link-${s.key}`}
              className="font-mono-spec text-[11px] tracking-[0.18em] uppercase text-[#51685A] hover:text-[#14703F] transition-colors duration-300 whitespace-nowrap"
            >
              {t.nav[s.key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle className="hidden sm:flex" />
          <a
            href={specHref}
            download={specName}
            data-testid="nav-contact-cta"
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-[rgba(20,112,63,0.4)] px-5 py-2.5 font-mono-spec text-[11px] tracking-[0.14em] uppercase text-[#14703F] hover:bg-[#14703F] hover:text-white transition-colors duration-300 whitespace-nowrap"
          >
            {t.nav.cta}
            <ArrowUpRight size={14} />
          </a>
          <button
            className="lg:hidden text-[#12261A] p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden backdrop-blur-xl bg-white/95 border-b border-[rgba(20,112,63,0.16)] px-6 py-6 flex flex-col gap-5"
          data-testid="nav-mobile-menu"
        >
          {SECTIONS.map((s) => (
            <a
              key={s.href}
              href={s.href}
              onClick={() => setOpen(false)}
              className="font-mono-spec text-xs tracking-[0.22em] uppercase text-[#51685A]"
            >
              {t.nav[s.key]}
            </a>
          ))}
          <a
            href={specHref}
            download={specName}
            onClick={() => setOpen(false)}
            className="font-mono-spec text-xs tracking-[0.22em] uppercase text-[#14703F]"
          >
            {t.nav.cta}
          </a>
          <LangToggle />
        </motion.div>
      )}
    </motion.header>
  );
};
